// A reply it cannot draw (02-DESIGN/03-console/01-the-console.md § conventions:
// a reply it cannot draw; hq issue 176). Every page, every tab and every panel
// a solution declares stands alone: one that throws while drawing what a reply
// holds — a reply whose shape is not the wire the console mirrors, from a
// service older or newer than the console — says so in its own place, with
// what failed in one quiet line and *Try again*, and the rail, the page's
// title and every other tab stay as they were. The console never goes blank
// because one reply was not what it expected.

import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button, describe } from "./components.tsx";

/** What a boundary stands for, in the words its notice says: *Could not show this page*. */
export type Part = "page" | "tab";

interface Props {
  /** What the boundary stands for. */
  part: Part;
  /**
   * The key the boundary is drawn for — the address of a page, the id of a
   * tab: another one is a fresh start, so moving on clears what failed.
   */
  at: string;
  children: ReactNode;
}

interface State {
  /** What the drawing threw; nothing while it draws. */
  failed?: { err: unknown };
}

/** couldNotShow is the notice's title for a part. */
export function couldNotShow(part: Part): string {
  return `Could not show this ${part}`;
}

/**
 * Boundary draws its children, or — once drawing them threw — the notice in
 * their place. *Try again* draws them again, which reads again; going to
 * another page or tab draws that one fresh.
 */
export class Boundary extends Component<Props, State> {
  override state: State = {};

  static getDerivedStateFromError(err: unknown): State {
    return { failed: { err } };
  }

  override componentDidCatch(err: unknown, info: ErrorInfo): void {
    // The browser's console keeps the whole trace for whoever reads it.
    console.error(couldNotShow(this.props.part), err, info.componentStack);
  }

  override componentDidUpdate(prev: Props): void {
    if (prev.at !== this.props.at && this.state.failed !== undefined) {
      this.retry();
    }
  }

  retry = (): void => {
    this.setState({ failed: undefined });
  };

  override render(): ReactNode {
    const { failed } = this.state;
    if (failed === undefined) {
      return this.props.children;
    }
    return <CouldNotShow part={this.props.part} err={failed.err} onRetry={this.retry} />;
  }
}

/** CouldNotShow is the notice a boundary draws in place of what failed. */
export function CouldNotShow({ part, err, onRetry }: { part: Part; err: unknown; onRetry: () => void }) {
  return (
    <div className="notice bad" role="alert">
      <div className="title">{couldNotShow(part)}</div>
      <div className="text mono faint">{describe(err)}</div>
      <div style={{ marginTop: 8 }}>
        <Button size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </div>
  );
}
